import React, { useState, useEffect, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import client from "@culturehq/client";
import { Icon, Thumbnail } from "@culturehq/components";

import { useProfile } from "app/context";
import { ProfileLink } from "app/links";

import { TimeDiff } from "components/common/Time";
import CommentsList from "components/comments/CommentsList";
import CommentsWrapper from "components/comments/CommentsWrapper";

import PhotoFaces from "./tags/PhotoFaces";
import PhotoTags from "./tags/PhotoTags";

const createComment = (createdComment, comments) => {
  if (!createdComment.parentCommentId) {
    return [createdComment, ...comments];
  }

  return [
    ...comments.map(comment => {
      if (comment.id !== createdComment.parentCommentId) {
        return comment;
      }

      return {
        ...comment,
        childComments: comment.childComments.concat(createdComment)
      };
    })
  ];
};

const deleteComment = (deletedComment, comments) => {
  const filter = comment => comment.id !== deletedComment.id;

  return comments.filter(filter).map(comment => ({
    ...comment,
    childComments: comment.childComments.filter(filter)
  }));
};

const toggleCommentCheer = (commentId, cheerForUser, cheersCount) => ({ comments }) => ({
  comments: comments.map(comment => {
    if (comment.id === commentId) {
      return { ...comment, cheerForUser, cheersCount };
    }

    return {
      ...comment,
      childComments: comment.childComments.map(child => (
        child.id === commentId ? { ...child, cheerForUser, cheersCount } : child
      ))
    };
  })
});

const LightboxPhoto = ({
  activePhoto,
  containerRef,
  actionablePhoto = true,
  tagging,
  onTagDelete,
  onTagStart,
  onPhotoCheerToggle
}) => {
  const imageRef = React.createRef();
  const [state, setState] = useState({
    commentCount: activePhoto.commentCount,
    hovering: false,
    showComments: false
  });
  const [boundingBox, setBoundingBox] = useState(null);
  const { profile } = useProfile();
  const [comments, setComments] = useState([]);
  const [showMore, setShowMore] = useState(true);
  const onToggleShowMore = () => setShowMore(value => !value);

  useEffect(
    () => {
      const computeBoundingBox = () => {
        const image = imageRef.current;

        const imageRect = image.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();

        setBoundingBox({
          left: imageRect.left - containerRect.left,
          top: imageRect.top - containerRect.top,
          width: imageRect.width,
          height: imageRect.height,
          containerLeft: containerRect.left,
          containerTop: containerRect.top
        });
      };

      if (tagging) {
        computeBoundingBox();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tagging]
  );

  useEffect(
    () => {
      const getComments = async () => {
        const { comments: results } = await client.listPhotoComments({ photoId: activePhoto.id });
        setComments(results);
      };
      getComments();
    },
    [activePhoto]
  );

  const handleCreateComment = comment => {
    setComments(createComment(comment, comments));
  };

  const handleDeletedComment = comment => {
    setComments(deleteComment(comment, comments));
  };

  useLayoutEffect(() => {
    const computeBoundingBox = () => {
      const image = imageRef.current;

      const imageRect = image.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      setBoundingBox({
        left: imageRect.left - containerRect.left,
        top: imageRect.top - containerRect.top,
        width: imageRect.width,
        height: imageRect.height,
        containerLeft: containerRect.left,
        containerTop: containerRect.top
      });
    };

    const updateSize = () => {
      computeBoundingBox();
    };
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [containerRef, imageRef]);

  const computeBoundingBox = () => {
    const image = imageRef.current;

    const imageRect = image.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    setBoundingBox({
      left: imageRect.left - containerRect.left,
      top: imageRect.top - containerRect.top,
      width: imageRect.width,
      height: imageRect.height,
      containerLeft: containerRect.left,
      containerTop: containerRect.top
    });
  };

  const handleImageLoad = () => {
    computeBoundingBox();
  };

  const handleCommentClick = () => {
    setState({ ...state, showComments: false });
  };

  const handleCommentsChange = commentCount => {
    setState({ ...state, commentCount });
  };

  const handleTagStart = event => {
    const { x, y, width, height } = event.target.getBoundingClientRect();

    event.stopPropagation();

    onTagStart({
      clientX: event.clientX,
      clientY: event.clientY,
      left: (event.clientX - x) / width,
      top: (event.clientY - y) / height
    });
  };

  const handleMouseEnter = () => {
    setState({ ...state, hovering: true });
  };

  const handleMouseLeave = () => {
    setState({ ...state, hovering: false });
  };

  const handleCheerCommentToggle = (commentId, cheerForUser, cheersCount) => {
    setComments(comments.map(comment => {
      if (comment.id === commentId) {
        return { ...comment, cheerForUser, cheersCount };
      }

      return {
        ...comment,
        childComments: comment.childComments.map(child => (
          child.id === commentId ? { ...child, cheerForUser, cheersCount } : child
        ))
      };
    }));
  };

  const { uploader, createdAt, image, caption, event } = activePhoto;
  return (
    <div
      className="gallery-lightbox__wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={`gallery-lightbox__photo ${actionablePhoto ? "col-sm-7" : ""} col-xs-12`}>
        <div className="gallery-lightbox__image-container" style={{ backgroundImage: `url(${image.largeSrc || image.largeUrl})` }}>
          {activePhoto.photoTags && activePhoto.photoTags.length > 0 && boundingBox && (
            <PhotoTags
              boundingBox={boundingBox}
              containerRef={containerRef}
              onTagDelete={onTagDelete}
              photo={activePhoto}
              tagging={tagging}
            />
          )}
          {(activePhoto.faces && activePhoto.faces.length > 0 && boundingBox)
            && tagging
            && (
              <PhotoFaces
                boundingBox={boundingBox}
                containerRef={containerRef}
                onTagStart={onTagStart}
                photo={activePhoto}
              />
            )}
        </div>
        <img
          className="gallery-lightbox__main-image"
          ref={imageRef}
          src={image.largeSrc || image.largeUrl}
          alt={caption}
          onClick={tagging ? handleTagStart : null}
          onLoad={handleImageLoad}
        />
      </div>

      {actionablePhoto && (
        <div className="gallery-lightbox__details-container col-sm-5 col-xs-12">
          <div className="scrollable-container no-scrollbar">
            <div className="gallery-lightbox__title-container">
              <Icon icon="calendar-filled" />
              <Link
                to={`/events/${event?.id}/photos`}
                className="link gallery-lightbox__event-name-link title"
              >
                {event?.name}
              </Link>
            </div>

            <hr />

            {uploader && (
              <div className="gallery-lightbox__creator-container">
                <Thumbnail className="Thumbnail" image={uploader.avatar.thumbUrl} title={uploader.name} />
                <div className="gallery-lightbox__creator-information">
                  <ProfileLink user={uploader} className="gallery-lightbox__uploader-name" />
                  <div className="gallery-lightbox__uploader-date">
                    <TimeDiff time={createdAt} />
                  </div>
                </div>
              </div>
            )}

            <CommentsList
              createCall={client.createPhotoComment}
              comments={comments}
              showAvatar={false}
              onCreateComment={handleCreateComment}
              onChange={handleCommentsChange}
              onCheerToggle={handleCheerCommentToggle}
              onDeleteComment={handleDeletedComment}
              options={{ photoId: activePhoto.id }}
              profile={profile}
              showMore={showMore}
              onToggleShowMore={onToggleShowMore}
            />
          </div>

          <CommentsWrapper
            commentCount={activePhoto.commentCount}
            createCall={client.createPhotoComment}
            onCommentClick={handleCommentClick}
            onChange={handleCommentsChange}
            onCreateComment={handleCreateComment}
            onCheerToggle={onPhotoCheerToggle}
            onToggleCommentCheer={toggleCommentCheer}
            options={{ photoId: activePhoto.id }}
            profile={profile}
            showAvatar={false}
            photo={activePhoto}
          />
        </div>
      )}
    </div>
  );
};

export default LightboxPhoto;
